<template>
  <BModal
    :model-value="show"
    centered
    :title="isEditMode ? 'Edit Exemption' : 'Add New Exemption'"
    @hide="$emit('close')"
  >
    <BAlert v-if="localError" variant="danger" :model-value="true" dismissible @dismissed="localError = null">
      <div v-for="err in (Array.isArray(localError) ? localError : [localError])" :key="err">
        {{ err }}
      </div>
    </BAlert>

    <form @submit.prevent="handleSave">
      <BFormGroup
        label="GCN / Clinical Formulation ID"
        label-for="clinical_formulation_id"
      >
        <template #label>
          GCN / Clinical Formulation ID <span class="text-danger">*</span>
        </template>
        <BFormInput
          id="clinical_formulation_id"
          v-model="formData.clinical_formulation_id"
          placeholder="Enter GCN or Clinical Formulation ID"
          required
        />
        <template #description>
          Leading 0's will be stripped to match downstream systems
        </template>
      </BFormGroup>

      <BFormGroup class="mt-3">
        <template #label>
          Minimum Age to Report
          <i
            class="fas fa-info-circle fa-sm text-muted"
            v-b-tooltip.hover
            title="Records are excluded if the patient is younger than this age. If no age is set, age is not considered."
            style="cursor: help;"
          ></i>
        </template>
        <BFormInput
          id="age_cutoff"
          aria-label="Minimum Age to Report"
          :model-value="formData.age_cutoff"
          @update:model-value="formData.age_cutoff = $event === '' || $event === null ? null : Number($event)"
          type="number"
          placeholder="Leave empty to exclude for all ages"
          min="0"
        />
        <template #description>
          {{ formData.age_cutoff === null ? 'Excluded for all ages' : `Patients under ${formData.age_cutoff} will be excluded` }}
        </template>
      </BFormGroup>
    </form>

    <template #footer>
      <BButton variant="secondary" @click="$emit('close')">Cancel</BButton>
      <BButton variant="primary" @click="handleSave" :disabled="actualLoading">
        <BSpinner v-if="actualLoading" small class="me-2" />
        {{ isEditMode ? 'Update' : 'Create' }}
      </BButton>
    </template>
  </BModal>
</template>

<script>
import axios from 'axios';
import { BModal, BAlert, BButton, BSpinner, BFormGroup, BFormInput, vBTooltip } from 'bootstrap-vue-next';

export default {
  components: { BModal, BAlert, BButton, BSpinner, BFormGroup, BFormInput },
  directives: { bTooltip: vBTooltip },
  props: {
    show: { type: Boolean, default: false },
    isEditMode: { type: Boolean, default: false },
    initialData: {
      type: Object,
      default: () => ({ clinical_formulation_id: '', age_cutoff: null })
    },
    pmpId: { type: Number, required: true }
  },
  data() {
    return {
      formData: { clinical_formulation_id: '', age_cutoff: null },
      localLoading: false,
      localError: null
    };
  },
  computed: {
    actualLoading() { return this.localLoading; }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.formData = { ...this.initialData };
        this.localError = null;
      }
    }
  },
  methods: {
    async handleSave() {
      this.localLoading = true;
      this.localError = null;

      try {
        const payload = {
          clinical_formulation_drug_exemption: {
            clinical_formulation_id: this.formData.clinical_formulation_id.trim(),
            age_cutoff: this.formData.age_cutoff
          }
        };

        if (this.isEditMode) {
          await axios.patch(`/pmps/clinical_formulation_drug_exemptions/${this.formData.id}?pmp_id=${this.pmpId}`, payload);
        } else {
          await axios.post(`/pmps/clinical_formulation_drug_exemptions?pmp_id=${this.pmpId}`, payload);
        }

        this.$emit('success', { isEditMode: this.isEditMode });
        this.$emit('close');
      } catch (err) {
        this.localError = err.response?.data?.error || 'An error occurred while saving the exemption';
      } finally {
        this.localLoading = false;
      }
    }
  }
};
</script>
